import { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  FileInput,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconDownload, IconFile, IconTrash } from "@tabler/icons-react";
import dayjs from "dayjs";
import useTransientStore from "../../../data/transcientStore";
import { useTranslation } from "../../../hooks/useTranslation";
import { usePermissions } from "../../../hooks/usePermissions";
import { ReportSectionHeader } from "../ReportSectionHeader";
import { getContractsByAddressSystemAndUser } from "../../../services/MapComponentsService";
import {
  deleteCityObjectReportFile,
  downloadCityObjectReportFileBlob,
  listCityObjectReportFileGroups,
  listCityObjectReportFiles,
  uploadCityObjectReportFile,
} from "../../../services/CityObjectReportFilesService";
import type { CityObjectReportFileListItemType } from "../../../types/CityObjectReportFile";
import type { ContractListItem } from "../../../types/UserRole";
import { notifications } from "@mantine/notifications";

type Screen = "list" | "add";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function truncateTitle(s: string, max = 48): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max)}…`;
}

interface ReportFilesReportProps {
  onBack: () => void;
  showHeader?: boolean;
}

export default function ReportFilesReport({
  onBack,
  showHeader = true,
}: Readonly<ReportFilesReportProps>) {
  const selectedAddressSystemId =
    useTransientStore((s) => s.selectedAddressSystem?.id) ?? 0;
  const currentUserId = useTransientStore((s) => s.currentUserId) ?? 0;
  const { getTranslation } = useTranslation({
    pageId: 512,
    addressSystemId: selectedAddressSystemId,
    autoLoad: true,
  });
  const { checkAccess } = usePermissions();

  const canShow = checkAccess("CITY_OBJECT_REPORT_FILES_SHOW");
  const canModify = checkAccess("CITY_OBJECT_REPORT_FILES_MODIFY");

  const [screen, setScreen] = useState<Screen>("list");
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<CityObjectReportFileListItemType[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [contracts, setContracts] = useState<ContractListItem[]>([]);
  const [contractsLoading, setContractsLoading] = useState(false);

  const [fileGroup, setFileGroup] = useState("");
  const [existingGroupChoice, setExistingGroupChoice] = useState<string | null>(
    null,
  );
  const [fileTitle, setFileTitle] = useState("");
  const [fileValue, setFileValue] = useState<File | null>(null);
  const [selectedContractIds, setSelectedContractIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const loadList = useCallback(async () => {
    if (selectedAddressSystemId <= 0) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await listCityObjectReportFiles(
        selectedAddressSystemId,
        currentUserId,
      );
      setRows(data);
    } catch (e) {
      console.error(e);
      notifications.show({
        title: getTranslation("REPORTS_ERROR") || "Error",
        message: e instanceof Error ? e.message : "Failed to load files",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  }, [selectedAddressSystemId, getTranslation]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const loadAddMeta = useCallback(async () => {
    if (selectedAddressSystemId <= 0 || currentUserId <= 0) return;
    setContractsLoading(true);
    try {
      const [g, c] = await Promise.all([
        listCityObjectReportFileGroups(selectedAddressSystemId, currentUserId),
        getContractsByAddressSystemAndUser(selectedAddressSystemId, currentUserId),
      ]);
      setGroups(g);
      setContracts(c);
      setSelectedContractIds(c.map((x) => x.id));
    } catch (e) {
      console.error(e);
      notifications.show({
        title: getTranslation("REPORTS_ERROR") || "Error",
        message: e instanceof Error ? e.message : "Failed to load form data",
        color: "red",
      });
    } finally {
      setContractsLoading(false);
    }
  }, [selectedAddressSystemId, currentUserId, getTranslation]);

  useEffect(() => {
    if (screen === "add") void loadAddMeta();
  }, [screen, loadAddMeta]);

  const handleDownload = async (row: CityObjectReportFileListItemType) => {
    try {
      const { blob, filename } = await downloadCityObjectReportFileBlob(
        selectedAddressSystemId,
        row.id,
        currentUserId,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || row.originalName || "file";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      notifications.show({
        title: getTranslation("REPORTS_ERROR") || "Error",
        message: e instanceof Error ? e.message : "Download failed",
        color: "red",
      });
    }
  };

  const handleDelete = (row: CityObjectReportFileListItemType) => {
    modals.openConfirmModal({
      title: getTranslation("REPORTS_FILES_DELETE_TITLE") || "Delete file",
      children: (
        <Text size="sm">
          {getTranslation("REPORTS_FILES_DELETE_BODY") ||
            "Remove this file from the list? This cannot be undone."}
        </Text>
      ),
      labels: {
        confirm: getTranslation("TEXT_BUTTON_POISTA") || "Delete",
        cancel: getTranslation("TEXT_BUTTON_PERUUTA") || "Cancel",
      },
      confirmProps: { color: "red" },
      onConfirm: () => {
        void (async () => {
          try {
            await deleteCityObjectReportFile(
              selectedAddressSystemId,
              row.id,
              currentUserId,
            );
            notifications.show({
              message:
                getTranslation("TEXT_ALERT_DELETE") || "Deleted.",
              color: "green",
            });
            void loadList();
          } catch (e) {
            console.error(e);
            notifications.show({
              title: getTranslation("REPORTS_ERROR") || "Error",
              message: e instanceof Error ? e.message : "Delete failed",
              color: "red",
            });
          }
        })();
      },
    });
  };

  const handleSubmitAdd = async () => {
    if (!fileGroup.trim()) {
      notifications.show({
        message:
          getTranslation("TEXT_ALERT_GROUP") || "Enter a group.",
        color: "yellow",
      });
      return;
    }
    if (!fileValue) {
      notifications.show({
        message:
          getTranslation("TEXT_ALERT_SELECT_FILE") || "Select a file.",
        color: "yellow",
      });
      return;
    }
    if (!fileTitle.trim()) {
      notifications.show({
        message:
          getTranslation("TEXT_ALERT_FILE_HEADER") || "Enter a file title.",
        color: "yellow",
      });
      return;
    }
    if (selectedContractIds.length === 0) {
      notifications.show({
        message:
          getTranslation("TEXT_ALERT_EI_SOPIMUSTA") ||
          "Select at least one contract.",
        color: "yellow",
      });
      return;
    }
    setSaving(true);
    try {
      await uploadCityObjectReportFile(selectedAddressSystemId, {
        file: fileValue,
        fileGroup: fileGroup.trim(),
        fileHeader: fileTitle.trim(),
        contractIds: selectedContractIds,
      }, currentUserId);
      notifications.show({
        message: getTranslation("TEXT_ALERT_SAVE") || "Saved.",
        color: "green",
      });
      setFileGroup("");
      setExistingGroupChoice(null);
      setFileTitle("");
      setFileValue(null);
      setScreen("list");
      void loadList();
    } catch (e) {
      console.error(e);
      notifications.show({
        title: getTranslation("REPORTS_ERROR") || "Error",
        message: e instanceof Error ? e.message : "Upload failed",
        color: "red",
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleContract = (id: number, checked: boolean) => {
    setSelectedContractIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((x) => x !== id);
    });
  };

  const selectAllContracts = () =>
    setSelectedContractIds(contracts.map((c) => c.id));
  const clearContracts = () => setSelectedContractIds([]);

  if (!canShow) {
    return (
      <Card withBorder radius="lg" px="xl" py="lg" shadow="sm">
        <Alert color="yellow">
          {getTranslation("REPORTS_FILES_NO_RIGHTS") ||
            "You need CITY_OBJECT_REPORT_FILES_SHOW to view report files."}
        </Alert>
      </Card>
    );
  }

  if (selectedAddressSystemId <= 0 || currentUserId <= 0) {
    return (
      <Card withBorder radius="lg" px="xl" py="lg" shadow="sm">
        <Text c="dimmed" size="sm">
          {getTranslation("PRINTOUTS_SELECT_CONTEXT") ||
            "Select an address system and sign in."}
        </Text>
      </Card>
    );
  }

  if (screen === "add") {
    return (
      <Card withBorder radius="lg" px="xl" py="lg" shadow="sm">
        <Stack gap="md">
          <ReportSectionHeader
            onBack={() => setScreen("list")}
            contextLabel={getTranslation("REPORTS_HUB_LABEL") || "Reports"}
            title={
              getTranslation("REPORTS_FILES_ADD_TITLE") || "Add a new file"
            }
            backLabel={
              getTranslation("REPORTS_FILES_BACK_TO_LIST") ||
              "Back to file list"
            }
          />

          {contractsLoading ? (
            <Group justify="center" p="md">
              <Loader />
            </Group>
          ) : (
            <Stack gap="md">
              <TextInput
                label={getTranslation("TEXT_LABEL_RYHMA") || "Group"}
                value={fileGroup}
                onChange={(e) => setFileGroup(e.currentTarget.value)}
                required
              />
              <Select
                label={
                  getTranslation("REPORTS_FILES_EXISTING_GROUPS") ||
                  "Existing groups"
                }
                placeholder={
                  getTranslation("REPORTS_FILES_NEW_GROUP_OPTION") ||
                  "Add a new group"
                }
                clearable
                data={groups.map((g) => ({ value: g, label: g }))}
                value={existingGroupChoice}
                onChange={(v) => {
                  setExistingGroupChoice(v);
                  if (v) setFileGroup(v);
                }}
              />
              <FileInput
                label={
                  getTranslation("TEXT_LABEL_KUVA_TAI_TIEDOSTO") || "File"
                }
                value={fileValue}
                onChange={setFileValue}
                leftSection={<IconFile size={18} />}
                clearable
                required
              />
              <TextInput
                label={
                  getTranslation("TEXT_LABEL_KUVA_TAI_TIEDOSTO_OTSIKKO") ||
                  "File title"
                }
                value={fileTitle}
                onChange={(e) => setFileTitle(e.currentTarget.value)}
                required
              />
              <div>
                <Group justify="space-between" mb={6}>
                  <Text size="sm" fw={600}>
                    {getTranslation("TEXT_LABEL_SOPIMUKSET") || "Contracts"}
                  </Text>
                  {contracts.length > 1 && (
                    <Group gap="xs">
                      <Button size="compact-xs" variant="light" onClick={selectAllContracts}>
                        {getTranslation("PRINTOUTS_CHECK_ALL") || "All"}
                      </Button>
                      <Button size="compact-xs" variant="light" onClick={clearContracts}>
                        {getTranslation("PRINTOUTS_UNCHECK_ALL") || "None"}
                      </Button>
                    </Group>
                  )}
                </Group>
                <Stack gap={6}>
                  {contracts.map((c) => (
                    <Checkbox
                      key={c.id}
                      label={c.name}
                      checked={selectedContractIds.includes(c.id)}
                      onChange={(e) =>
                        toggleContract(c.id, e.currentTarget.checked)
                      }
                    />
                  ))}
                </Stack>
              </div>
              <Button loading={saving} onClick={() => void handleSubmitAdd()}>
                {getTranslation("TEXT_BUTTON_LAHETA") || "Save"}
              </Button>
            </Stack>
          )}
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder radius="lg" px="xl" py="lg" shadow="sm">
      <Stack gap="md">
        {showHeader && (
          <ReportSectionHeader
            onBack={onBack}
            contextLabel={getTranslation("REPORTS_HUB_LABEL") || "Reports"}
            title={getTranslation("REPORTS_FILES") || "Files"}
            backLabel={getTranslation("REPORTS_RETURN") || "Back to reports"}
          />
        )}

        {canModify && (
          <Group justify="flex-end">
            <Button onClick={() => setScreen("add")}>
              {getTranslation("REPORTS_FILES_ADD_NEW") || "Add new"}
            </Button>
          </Group>
        )}

        {loading && (
          <Group justify="center" p="md">
            <Loader />
          </Group>
        )}
        {!loading && rows.length === 0 && (
          <Text c="dimmed" size="sm">
            {getTranslation("REPORTS_FILES_EMPTY") || "No files."}
          </Text>
        )}
        {!loading && rows.length > 0 && (
          <Box mt="md">
            <Paper withBorder p="md">
              <Title order={6} c="blue" mb="md">
                {getTranslation("LABEL_SEARCH_RESULTS_TITLE") || "Files"}{" "}
                ({rows.length})
              </Title>
              <Stack gap="xs">
                {rows.map((row) => (
                  <Paper key={row.id} p="sm" withBorder>
                    <Group
                      justify="space-between"
                      align="flex-start"
                      wrap="nowrap"
                      gap="sm"
                    >
                      <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                        <Group gap="xs" wrap="nowrap">
                          <Text size="xs" fw={700} c="dimmed" style={{ flexShrink: 0 }}>
                            {getTranslation("TEXT_LABEL_RYHMA") || "Group"}
                          </Text>
                          <Text size="sm" style={{ wordBreak: "break-word" }}>
                            {row.fileGroup || "—"}
                          </Text>
                        </Group>
                        <Text
                          fw={600}
                          size="md"
                          lineClamp={2}
                          title={row.originalName}
                          style={{ wordBreak: "break-word", lineHeight: 1.35 }}
                        >
                          {truncateTitle(row.title || row.originalName)}
                        </Text>
                        <Group gap="md" wrap="wrap">
                          <Text size="xs" c="dimmed">
                            <b>{getTranslation("TEXT_LABEL_PVM") || "Date"}:</b>{" "}
                            {row.created
                              ? dayjs(row.created).format("DD.MM.YYYY")
                              : "—"}
                          </Text>
                          <Text size="xs" c="dimmed">
                            <b>{getTranslation("TEXT_LABEL_KOKO") || "Size"}:</b>{" "}
                            {formatFileSize(row.sizeBytes)}
                          </Text>
                        </Group>
                      </Stack>
                      <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                        <ActionIcon
                          variant="light"
                          color="blue"
                          title={row.originalName}
                          aria-label={
                            getTranslation("TEXT_BUTTON_LATAA") || "Download"
                          }
                          onClick={() => void handleDownload(row)}
                        >
                          <IconDownload size={18} />
                        </ActionIcon>
                        {canModify && (
                          <ActionIcon
                            variant="light"
                            color="red"
                            aria-label={
                              getTranslation("TEXT_BUTTON_POISTA") || "Delete"
                            }
                            onClick={() => handleDelete(row)}
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                        )}
                      </Group>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Box>
        )}
      </Stack>
    </Card>
  );
}
